import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomDynamicFormState } from "@midland-industries/Store/Data/DynamicForms/DynamicFormReducer";
import removeDynamicFormFieldValue from "@midland-industries/Store/Data/DynamicForms/Handlers/RemoveDynamicFormFieldValue";
import setDynamicFormFieldValue from "@midland-industries/Store/Data/DynamicForms/Handlers/SetDynamicFormFieldValue";
import { DynamicFormButtonShapes, DynamicFormButtonShapesPill, DynamicFormButtonShapesRectangle, DynamicFormButtonShapesRounded, DynamicFormButtonTypes, DynamicFormButtonTypesOutline, DynamicFormButtonTypesSolid, DynamicFormButtonVariants, DynamicFormButtonVariantsPrimary, DynamicFormButtonVariantsSecondary, DynamicFormButtonVariantsTertiary, DynamicFormEmailRegExp, DynamicFormFileUploadLabelPositionsNone, DynamicFormIntegerFieldLabelPositionsNone, DynamicFormTextFieldLabelPositionsNone } from "@midland-industries/Types/DynamicFormModels";
import { determineFormFieldIndexes } from "@midland-industries/Utilities/DetermineFormFieldIndexes";
import DynamicForm from "@midland-industries/Widgets/DynamicForms/DynamicForm";
import DynamicFormFileUpload from "@midland-industries/Widgets/DynamicForms/DynamicFormFileUpload";
import DynamicFormIntegerField from "@midland-industries/Widgets/DynamicForms/DynamicFormIntegerField";
import DynamicFormSelect from "@midland-industries/Widgets/DynamicForms/DynamicFormSelect";
import DynamicFormTextField from "@midland-industries/Widgets/DynamicForms/DynamicFormTextField";
import React from "react";
import { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    formVariant = "formVariant",
    includeBlackWhiteOption = "includeBlackWhiteOption",
    blackWhitePrintPrice = "blackWhitePrintPrice",
    colorPrintPrice = "colorPrintPrice",
    maxOrderQuantity = "maxOrderQuantity",
    showSuccessLandingScreen = "showSuccessLandingScreen",
    maxLogoSizeMB = "maxLogoSizeMB",
    maxPricingFileSizeMB = "maxPricingFileSizeMB",
    pricingSheetsLinkDestination = "pricingSheetsLinkDestination",
    emailRecipients = "emailRecipients",
    submitButtonText = "submitButtonText",
    successMessage = "successMessage",
    buttonVariant = "buttonVariant",
    buttonShape = "buttonShape",
    buttonType = "buttonType",
    enableRecaptcha = "enableRecaptcha",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.formVariant]: string;
        [fields.includeBlackWhiteOption]: boolean;
        [fields.blackWhitePrintPrice]: number;
        [fields.colorPrintPrice]: number;
        [fields.maxOrderQuantity]: number;
        [fields.showSuccessLandingScreen]: boolean;
        [fields.maxLogoSizeMB]: number;
        [fields.maxPricingFileSizeMB]: number;
        [fields.pricingSheetsLinkDestination]: LinkFieldValue;
        [fields.emailRecipients]: string;
        [fields.submitButtonText]: string;
        [fields.successMessage]: string;
        [fields.buttonVariant]: DynamicFormButtonVariants;
        [fields.buttonShape]: DynamicFormButtonShapes;
        [fields.buttonType]: DynamicFormButtonTypes;
        [fields.enableRecaptcha]: boolean;
    };
}

const mapStateToProps = (state: CustomDynamicFormState) => {
    const dynamicFormData = state.xcentiumDynamicForms;
    return {
        dynamicFormData,
        currentLocation: getLocation(state),
        isAuthenticatedUser: state.context.session.isAuthenticated && !state.context.session.isGuest,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
    removeDynamicFormFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface CustomCatalogFormStyles {
    wrapper: InjectableCss;
    successWrapper: InjectableCss;
    zoneWrapper: InjectableCss;
    subTitle: TypographyPresentationProps;
    container: GridContainerProps;
    gridItemTwelve: GridItemProps;
    gridItemNine: GridItemProps;
    gridItemSix: GridItemProps;
    itemWrapper: InjectableCss;
    itemWrapperThin: InjectableCss;
    itemWrapperNoExtras: InjectableCss;
    itemHeaderWrapper: InjectableCss;
    largerItemHeaderWrapper: InjectableCss;
    logoHeaderTitleWrapper: InjectableCss;
    itemHeaderTitleWrapper: InjectableCss;
    itemHeaderTitle: TypographyPresentationProps;
    itemHeaderTextWrapper: InjectableCss;
    itemHeaderText: TypographyPresentationProps;
    successTitleWrapper: InjectableCss;
    successTitle: TypographyPresentationProps;
    invertedWrapper: InjectableCss;
    unauthenticatedWrapper: InjectableCss;
    unauthenticatedHeader: TypographyPresentationProps;
    loginButton: ButtonPresentationProps;
}

export const customCatalogFormStyles: CustomCatalogFormStyles = {
    wrapper: {
        css: css`
            width: 100%;
            padding-bottom: 30px;
        `,
    },
    successWrapper: {
        css: css`
            width: 100%;
            padding-bottom: 30px;
        `,
    },
    container: {
        gap: 0,
    },
    gridItemTwelve: {
        width: 12,
    },
    gridItemNine: {
        width: 9,
    },
    gridItemSix: {
        width: 6,
    },
    zoneWrapper: {
        css: css`
            width: 100%;
        `,
    },
    subTitle: {
        css: css`
            font-size: 28px;
            font-weight: 700;
            display: block;
            padding-bottom: 10px;
        `,
    },
    successTitleWrapper: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
        `,
    },
    successTitle: {
        css: css`
            font-size: 32px;
            font-weight: 600;
            display: block;
            padding: 30px 0;
        `,
    },
    itemWrapper: {
        css: css`
            width: 100%;
            padding-bottom: 10px;
            margin-bottom: 10px;
        `,
    },
    itemWrapperThin: {
        css: css`
            width: 100%;
            margin-bottom: 10px;
        `,
    },
    itemWrapperNoExtras: {
        css: css`
            width: 100%;
        `,
    },
    itemHeaderWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            width: 100%;
            padding-bottom: 10px;
        `,
    },
    largerItemHeaderWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            width: 100%;
            padding-bottom: 10px;
            margin-bottom: 10px;
        `,
    },
    logoHeaderTitleWrapper: {
        css: css`
            width: 100%;
            text-align: center;
        `,
    },
    itemHeaderTitleWrapper: {
        css: css`
            width: 100%;
            margin-bottom: 5px;
        `,
    },
    itemHeaderTitle: {
        css: css`
            font-size: 15px;
            font-weight: 600;
            min-height: 22px;
            width: 100%;
        `,
    },
    itemHeaderTextWrapper: {
        css: css`
            width: 100%;
        `,
    },
    itemHeaderText: {
        css: css`
            width: 100%;
        `,
    },
    invertedWrapper: {
        css: css`
            width: 100%;
            margin-bottom: 30px;
        `,
    },
    unauthenticatedWrapper: {
        css: css`
            width: 100%;
        `,
    },
    unauthenticatedHeader: {
        css: css`
            font-weight: 600;
        `,
    },
    loginButton: {
        css: css`
            background-color: ${getColor("secondary")};
        `,
    },
};

const styles = customCatalogFormStyles;

const catalogTypeFullProducts = "Full Products Catalog";
const catalogTypePlumbingPvf = "Plumbing and PVF Catalog";
const catalogTypeWaterworksIrrigation = "Waterworks and Irrigation Catalog";
const catalogTypeTruckTrailer = "Truck and Trailer Catalog";

const CustomCatalogForm: FC<Props> = props => {
    const {
        id,
        isAuthenticatedUser,
        history,
        signInUrl,
        currentLocation,
        fields,
        dynamicFormData,
        removeDynamicFormFieldValue,
    } = props;
    const {
        formVariant,
        includeBlackWhiteOption,
        blackWhitePrintPrice,
        colorPrintPrice,
        maxOrderQuantity,
        showSuccessLandingScreen,
        maxLogoSizeMB,
        maxPricingFileSizeMB,
        pricingSheetsLinkDestination,
        emailRecipients,
        submitButtonText,
        successMessage,
        buttonVariant,
        buttonShape,
        buttonType,
        enableRecaptcha,
    } = fields;

    const [selectedCatalogOption, setSelectedCatalogOption] = useState<string>("");
    const [minimumOrderQuantity, setMinimumOrderQuantity] = useState<number>(1);
    const [maximumOrderQuantity, setMaximumOrderQuantity] = useState<number>(1);
    const [successfulSubmit, setSuccessfulSubmit] = useState<boolean>(false);

    const { url } = useGetLink(pricingSheetsLinkDestination);

    if (successfulSubmit) {
        return (
            <StyledWrapper {...styles.wrapper} data-test-selector="CustomCatalogForm_wrapper">
                <StyledWrapper {...styles.itemHeaderWrapper}>
                    <Typography {...styles.subTitle}>{formVariant}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.successTitleWrapper}>
                    <Typography {...styles.successTitle}>Thank You For Your Order</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.invertedWrapper}>
                    <Typography variant="h5" className="mi-h5">
                        Custom Catalog Order Complete
                    </Typography>
                </StyledWrapper>

                <StyledWrapper {...styles.itemWrapper}>
                    <Typography>
                        Our designers have received your Custom Catalog Order and files and will be in contact with
                        digital proofs for your approval soon!
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.itemWrapper}>
                    <Typography>Thank you!</Typography>
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    const handleLoginButtonClick = (e: React.MouseEvent) => {
        const returnUrl = currentLocation.pathname + currentLocation.search;
        if (returnUrl === "/") {
            history.push(`${signInUrl}`);
        } else {
            history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
        }

        e.preventDefault();
    };

    if (!isAuthenticatedUser) {
        return (
            <StyledWrapper {...styles.wrapper} data-test-selector="CustomCatalogForm_wrapper">
                <StyledWrapper {...styles.itemWrapper}>
                    <Typography {...styles.unauthenticatedHeader}>
                        You must be logged in to an account before you can access any of the Custom Catalog order forms.
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.itemWrapper}>
                    <Typography>
                        Click the Login button to create an account or log in to your existing account.
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.unauthenticatedWrapper}>
                    <Button {...styles.loginButton} onClick={handleLoginButtonClick}>
                        {translate("Sign In")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        );
    }
    const customFormName = formVariant;

    const catalogOptionDigital = "Free Custom Digital";
    const catalogOptionBlackWhite = `Full Customized Black & White - $${blackWhitePrintPrice} each`;
    const catalogOptionColor = `Full Customized Color - $${colorPrintPrice} each`;

    const handleCatalogOptionChange: React.ChangeEventHandler<HTMLSelectElement> = event => {
        const newCatalogOption = event.target.value;

        switch (newCatalogOption) {
            case "digital":
                updateMinimumQuantity(1, 1);
                break;
            case "black-white":
                setMinimumOrderQuantity(25);
                setMaximumOrderQuantity(99999);
                break;
            case "color":
                setMinimumOrderQuantity(25);
                setMaximumOrderQuantity(99999);
                break;
            default:
                break;
        }

        // for removing items from the form...
        removeDynamicFormElementsFromState(newCatalogOption);

        setSelectedCatalogOption(newCatalogOption);
    };

    const updateMinimumQuantity = (minQty: number, maxQty: number) => {
        // set the local state variable that defines the dropdownlist start value.
        setMinimumOrderQuantity(minQty);
        setMaximumOrderQuantity(maxQty);

        // update the widget in dynamicFormsState
        const { formIndex, fieldIndex } = determineFormFieldIndexes(customFormName, "Order Quantity", dynamicFormData);
        if (formIndex === -1 || fieldIndex === -1) {
            return;
        }
        const currentField = dynamicFormData.forms[formIndex].fields[fieldIndex];
        if (currentField.value && Number(String(currentField.value)) < minQty) {
            setDynamicFormFieldValue({
                formName: customFormName,
                formField: {
                    key: currentField.key,
                    group: currentField.group,
                    label: currentField.label,
                    isRequired: currentField.isRequired,
                    value: minQty,
                    type: currentField.type,
                    changeTrueFalseToYesNo: currentField.changeTrueFalseToYesNo,
                    sendOnlyIfChecked: currentField.sendOnlyIfChecked,
                    hasError: currentField.hasError,
                    error: currentField.error,
                },
            });
        }
    };

    const removeDynamicFormElementsFromState = (newCatalogOption: string) => {
        switch (newCatalogOption) {
            case "digital":
                removeDynamicFormFieldValue({
                    formName: customFormName,
                    formFieldKey: "Footer Information 1",
                });
                removeDynamicFormFieldValue({
                    formName: customFormName,
                    formFieldKey: "Footer Information 2",
                });
                removeDynamicFormFieldValue({
                    formName: customFormName,
                    formFieldKey: "Footer Information 3",
                });
                removeDynamicFormFieldValue({
                    formName: customFormName,
                    formFieldKey: "Footer Information 4",
                });
                removeDynamicFormFieldValue({
                    formName: customFormName,
                    formFieldKey: "Pricing File",
                });
                break;
            default:
                break;
        }
    };

    const catalogOptions = [];
    catalogOptions.push({
        fields: { label: catalogOptionDigital, value: "digital" },
    });
    if (includeBlackWhiteOption) {
        catalogOptions.push({
            fields: { label: catalogOptionBlackWhite, value: "black-white" },
        });
    }
    catalogOptions.push({
        fields: { label: catalogOptionColor, value: "color" },
    });

    const orderQuantityOptions = [];
    for (let x = minimumOrderQuantity; x <= maxOrderQuantity; x++) {
        orderQuantityOptions.push({
            fields: { label: x.toString(), value: x.toString() },
        });
    }

    const orderQuantityMessage =
        selectedCatalogOption === "digital"
            ? "Order Quantity will always be 1 for digital catalogs"
            : `Minimum Order Quantity for selected Catalog Option: ${minimumOrderQuantity}`;

    const onSuccessfulSubmit = () => {
        setSuccessfulSubmit(true);
    };

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="CustomCatalogForm_wrapper">
            <StyledWrapper {...styles.itemHeaderWrapper}>
                <Typography {...styles.subTitle}>{formVariant}</Typography>
            </StyledWrapper>
            <DynamicForm
                id={id}
                onSuccessfulSubmit={onSuccessfulSubmit}
                skipModalOnSuccess={showSuccessLandingScreen}
                fields={{
                    formName: customFormName,
                    emailRecipients,
                    submitButtonText,
                    successMessage,
                    buttonVariant,
                    buttonShape,
                    buttonType,
                    showResetButton: false,
                    enableRecaptcha,
                    sendToThirdPartyApi: false,
                    sendToApiType: "custom-api",
                    customApiEndpoint: "",
                    endpointRequiresAuth: false,
                    endpointUserId: "",
                    endpointPassword: "",
                    displayCustomApiResponse: false,
                }}
            >
                <StyledWrapper {...styles.itemWrapper}>
                    <GridContainer {...styles.container}>
                        <GridItem {...styles.gridItemTwelve}>
                            <StyledWrapper {...styles.itemHeaderWrapper}>
                                <Typography {...styles.itemHeaderTitle}>Catalog Option *</Typography>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.gridItemNine}>
                            <DynamicFormSelect
                                handleOnChange={handleCatalogOptionChange}
                                fields={{
                                    key: "Catalog Option",
                                    hasLabel: false,
                                    label: "Catalog Option",
                                    hintText: "-- Select Catalog Option",
                                    isRequired: true,
                                    options: catalogOptions,
                                }}
                            />
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>

                <StyledWrapper {...styles.itemWrapper}>
                    <GridContainer {...styles.container}>
                        <GridItem {...styles.gridItemTwelve}>
                            <StyledWrapper {...styles.itemHeaderWrapper}>
                                <Typography {...styles.itemHeaderTitle}>Order Quantity *</Typography>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.gridItemNine}>
                            <DynamicFormIntegerField
                                fields={{
                                    key: "Order Quantity",
                                    labelPosition: DynamicFormIntegerFieldLabelPositionsNone,
                                    label: "Order Quantity",
                                    isRequired: true,
                                    hintText: "",
                                    min: minimumOrderQuantity,
                                    max: maximumOrderQuantity,
                                }}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItemNine}>
                            {selectedCatalogOption && selectedCatalogOption.length > 0 && (
                                <Typography>{orderQuantityMessage}</Typography>
                            )}
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>

                <StyledWrapper {...styles.itemWrapperNoExtras}>
                    <GridContainer {...styles.container}>
                        <GridItem {...styles.gridItemTwelve}>
                            <StyledWrapper {...styles.itemHeaderWrapper}>
                                <StyledWrapper {...styles.itemHeaderTitleWrapper}>
                                    <Typography {...styles.itemHeaderTitle}>Logo File Upload *</Typography>
                                </StyledWrapper>
                                <StyledWrapper {...styles.itemHeaderTextWrapper}>
                                    <Typography {...styles.itemHeaderText}>
                                        Please upload a high quality logo file.
                                    </Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.gridItemNine}>
                            <DynamicFormFileUpload
                                maxFileSizeMB={maxLogoSizeMB}
                                fields={{
                                    key: "Logo File",
                                    labelPosition: DynamicFormFileUploadLabelPositionsNone,
                                    label: "Logo File",
                                    isRequired: true,
                                    allowedFileExtensions: [
                                        { fields: { fileExtension: "png" } },
                                        { fields: { fileExtension: "jpg" } },
                                        { fields: { fileExtension: "eps" } },
                                        { fields: { fileExtension: "psd" } },
                                        { fields: { fileExtension: "pdf" } },
                                    ],
                                }}
                            />
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>

                {selectedCatalogOption !== "digital" && (
                    <StyledWrapper {...styles.itemWrapper}>
                        <GridContainer {...styles.container}>
                            <GridItem {...styles.gridItemTwelve}>
                                <StyledWrapper {...styles.itemHeaderWrapper}>
                                    <StyledWrapper {...styles.itemHeaderTitleWrapper}>
                                        <Typography {...styles.itemHeaderTitle}>Footer Information</Typography>
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.itemHeaderTextWrapper}>
                                        <Typography {...styles.itemHeaderText}>
                                            Please include your four custom footer options you'd like featured in your
                                            catalog, referenced in bullet 2.
                                        </Typography>
                                        <br />
                                        <Typography {...styles.itemHeaderText}>
                                            Each footer option field holds 34 characters, including spacing.
                                        </Typography>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.gridItemNine}>
                                <StyledWrapper {...styles.itemWrapperThin}>
                                    <DynamicFormTextField
                                        fields={{
                                            key: "Footer Information 1",
                                            labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                            label: "Footer Information 1",
                                            hintText: "",
                                            isRequired: false,
                                            enableMinLength: false,
                                            minLength: 0,
                                            enableMaxLength: true,
                                            maxLength: 34,
                                            validateAsPhoneNumber: false,
                                            validateAsEmailAddress: false,
                                        }}
                                    />
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.gridItemNine}>
                                <StyledWrapper {...styles.itemWrapperThin}>
                                    <DynamicFormTextField
                                        fields={{
                                            key: "Footer Information 2",
                                            labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                            label: "Footer Information 2",
                                            hintText: "",
                                            isRequired: false,
                                            enableMinLength: false,
                                            minLength: 0,
                                            enableMaxLength: true,
                                            maxLength: 34,
                                            validateAsPhoneNumber: false,
                                            validateAsEmailAddress: false,
                                        }}
                                    />
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.gridItemNine}>
                                <StyledWrapper {...styles.itemWrapperThin}>
                                    <DynamicFormTextField
                                        fields={{
                                            key: "Footer Information 3",
                                            labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                            label: "Footer Information 3",
                                            hintText: "",
                                            isRequired: false,
                                            enableMinLength: false,
                                            minLength: 0,
                                            enableMaxLength: true,
                                            maxLength: 34,
                                            validateAsPhoneNumber: false,
                                            validateAsEmailAddress: false,
                                        }}
                                    />
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.gridItemNine}>
                                <StyledWrapper {...styles.itemWrapperThin}>
                                    <DynamicFormTextField
                                        fields={{
                                            key: "Footer Information 4",
                                            labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                            label: "Footer Information 4",
                                            hintText: "",
                                            isRequired: false,
                                            enableMinLength: false,
                                            minLength: 0,
                                            enableMaxLength: true,
                                            maxLength: 34,
                                            validateAsPhoneNumber: false,
                                            validateAsEmailAddress: false,
                                        }}
                                    />
                                </StyledWrapper>
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                )}

                <StyledWrapper {...styles.itemWrapper}>
                    <GridContainer {...styles.container}>
                        <GridItem {...styles.gridItemTwelve}>
                            <StyledWrapper {...styles.itemHeaderWrapper}>
                                <StyledWrapper {...styles.itemHeaderTitleWrapper}>
                                    <Typography {...styles.itemHeaderTitle}>Contact Information *</Typography>
                                </StyledWrapper>
                                <StyledWrapper {...styles.itemHeaderTextWrapper}>
                                    <Typography {...styles.itemHeaderText}>
                                        For proofing or design questions, please include a contact email so our
                                        designers may contact you.
                                    </Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.gridItemNine}>
                            <DynamicFormTextField
                                fields={{
                                    key: "Contact Information",
                                    labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                    label: "Contact Information",
                                    hintText: "",
                                    isRequired: true,
                                    enableMinLength: false,
                                    minLength: 0,
                                    enableMaxLength: true,
                                    maxLength: 300,
                                    validateAsPhoneNumber: false,
                                    validateAsEmailAddress: false,
                                }}
                            />
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            </DynamicForm>
        </StyledWrapper>
    );
};

const formTab = {
    displayName: "Form",
    sortOrder: 0,
};

const buttonTab = {
    displayName: "Button",
    sortOrder: 1,
};

const customTab = {
    displayName: "Custom",
    sortOrder: 2,
};

/*

        [fields.includeBlackWhiteOption]: boolean;
        [fields.blackWhitePrintPrice]: number,
        [fields.colorPrintPrice]: number,

*/

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CustomCatalogForm)),
    definition: {
        group: "Basic",
        displayName: "Custom Catalog Form",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.formVariant,
                displayName: "Select Custom Catalog Variant",
                editorTemplate: "DropDownField",
                defaultValue: catalogTypeFullProducts,
                fieldType: "General",
                options: [
                    {
                        displayName: "Full Products Catalog",
                        value: catalogTypeFullProducts,
                    },
                    {
                        displayName: "Plumbing & PVF Catalog",
                        value: catalogTypePlumbingPvf,
                    },
                    {
                        displayName: "Waterworks & Irrigation Catalog",
                        value: catalogTypeWaterworksIrrigation,
                    },
                    {
                        displayName: "Truck & Trailer Catalog",
                        value: catalogTypeTruckTrailer,
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: customTab,
                sortOrder: 1,
            },
            {
                name: fields.includeBlackWhiteOption,
                displayName: "Include Black & White Catalog Option",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 2,
                tab: customTab,
            },
            {
                name: fields.blackWhitePrintPrice,
                displayName: "Black & White Catalog Print Price (EA)",
                editorTemplate: "IntegerField",
                fieldType: "General",
                defaultValue: 0,
                sortOrder: 3,
                isVisible: item => item.fields[fields.includeBlackWhiteOption] === true,
                tab: customTab,
            },
            {
                name: fields.colorPrintPrice,
                displayName: "Color Catalog Print Price (EA)",
                editorTemplate: "IntegerField",
                fieldType: "General",
                defaultValue: 0,
                sortOrder: 4,
                tab: customTab,
            },
            {
                name: fields.maxOrderQuantity,
                displayName: "Maximum Catalog Order Quantity",
                editorTemplate: "IntegerField",
                fieldType: "General",
                defaultValue: 50,
                sortOrder: 5,
                tab: customTab,
            },
            {
                name: fields.showSuccessLandingScreen,
                displayName: "Show Success Landing screen",
                tooltip: "Selecting this replaces the modal success message",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: true,
                sortOrder: 6,
                tab: customTab,
            },
            {
                name: fields.maxLogoSizeMB,
                displayName: "Max Logo Image Size (MB)",
                editorTemplate: "IntegerField",
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 7,
                tab: customTab,
            },
            {
                name: fields.pricingSheetsLinkDestination,
                displayName: "Pricing Sheets Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isRequired: true,
                tab: customTab,
                sortOrder: 9,
            },
            {
                name: fields.emailRecipients,
                displayName: "Email recipients",
                editorTemplate: "MultilineTextField",
                fieldType: "General",
                defaultValue: "testaccount@insitesoft.com",
                isRequired: true,
                regularExpression: DynamicFormEmailRegExp,
                tab: formTab,
                sortOrder: 20,
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
                tab: formTab,
                sortOrder: 30,
                isVisible: item => item.fields[fields.showSuccessLandingScreen] === false,
            },
            {
                name: fields.enableRecaptcha,
                displayName: "Include ReCaptcha",
                tooltip: "Enabling this setting adds a ReCaptcha element to the form which must be clicked to submit.",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 40,
                tab: formTab,
            },
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Submit",
                tab: buttonTab,
                sortOrder: 100,
            },
            {
                name: fields.buttonVariant,
                displayName: "Select Button Variant",
                editorTemplate: "DropDownField",
                defaultValue: DynamicFormButtonVariantsPrimary,
                fieldType: "General",
                options: [
                    {
                        displayName: "Primary",
                        value: DynamicFormButtonVariantsPrimary,
                    },
                    {
                        displayName: "Secondary",
                        value: DynamicFormButtonVariantsSecondary,
                    },
                    {
                        displayName: "Tertiary",
                        value: DynamicFormButtonVariantsTertiary,
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 110,
            },
            {
                name: fields.buttonShape,
                displayName: "Select Button Shape",
                editorTemplate: "DropDownField",
                defaultValue: DynamicFormButtonShapesRectangle,
                fieldType: "General",
                options: [
                    {
                        displayName: "Rectangle",
                        value: DynamicFormButtonShapesRectangle,
                    },
                    {
                        displayName: "Pill",
                        value: DynamicFormButtonShapesPill,
                    },
                    {
                        displayName: "Rounded",
                        value: DynamicFormButtonShapesRounded,
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 120,
            },
            {
                name: fields.buttonType,
                displayName: "Select Button Type",
                editorTemplate: "DropDownField",
                defaultValue: DynamicFormButtonTypesSolid,
                fieldType: "General",
                options: [
                    {
                        displayName: "Solid",
                        value: DynamicFormButtonTypesSolid,
                    },
                    {
                        displayName: "Outline",
                        value: DynamicFormButtonTypesOutline,
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 130,
            },
        ],
    },
};

export default widgetModule;
